import { useSelector } from 'react-redux'
import { Text } from 'common-components'

import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import * as Styled from './styled'

import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { memo, useMemo, useEffect, useRef } from 'react'
import OptionDropdown from './OptionDropdown'
import ImageAspectRatioDropdown from './ImageAspectRatioDropdown'
import { DisabledWrapper } from '../DisabledWrapper'
import { useActions } from './useActions'

const ModelSection = () => {
  const aiVideoModels = useSelector(aiVideoSelectors.aiVideoModels)
  const selectedModel = useSelector(aiVideoSelectors.selectedModel)
  const duration = useSelector(aiVideoSelectors.selectedDurationOption)
  const aiVideoAspectRatio = useSelector(aiVideoSelectors.aspectRatio)
  const defaultDimensions = useSelector(projectDataSelectors.defaultDimensions)
  const quality = useSelector(aiVideoSelectors.quality)
  const hasRefItemsInActiveBucket = useSelector(
    aiVideoSelectors.hasRefItemsInActiveBucket
  )

  const availableDurationOptions = useSelector(
    aiVideoSelectors.availableDurationOptions
  )
  const availableAspectRatioOptions = useSelector(
    aiVideoSelectors.availableAspectRatioOptions
  )
  const hasAspectRatioOptions = useSelector(
    aiVideoSelectors.hasAspectRatioOptions
  )
  const availableQualityOptions = useSelector(
    aiVideoSelectors.availableQualityOptions
  )

  const hasUploadedImage = useSelector(aiVideoSelectors.hasUploadedImage)
  const isMediaResizingSupported =
    selectedModel?.capabilities.isMediaResizingSupported
  const isAspectRatioVisible = useMemo(
    () =>
      hasAspectRatioOptions &&
      (isMediaResizingSupported ||
        !hasRefItemsInActiveBucket ||
        hasUploadedImage),
    [
      isMediaResizingSupported,
      hasAspectRatioOptions,
      hasRefItemsInActiveBucket,
      hasUploadedImage
    ]
  )

  const {
    handleModelChange,
    handleDurationChange,
    handleAspectRatioChange,
    handleImageAspectRatioChange,
    handleQualityChange
  } = useActions()

  const hasUploadedImageRef = useRef(hasUploadedImage)
  hasUploadedImageRef.current = hasUploadedImage
  const aiVideoAspectRatioRef = useRef(aiVideoAspectRatio)
  aiVideoAspectRatioRef.current = aiVideoAspectRatio

  useEffect(() => {
    if (
      defaultDimensions?.aspectRatioString &&
      !(hasUploadedImageRef.current && aiVideoAspectRatioRef.current === null)
    ) {
      handleAspectRatioChange(defaultDimensions.aspectRatioString)
    }
  }, [defaultDimensions?.aspectRatioString, handleAspectRatioChange])

  return (
    <DisabledWrapper>
      <Styled.ModelSection>
        <Text type="h2" fontWeight="600" color="secondaryDark">
          {translate(T.MODEL_LABEL)}
        </Text>
        <Styled.DropdownWrapper>
          <Styled.VideoModelDropdown
            models={aiVideoModels}
            selectedModelCode={selectedModel?.model}
            onModelSelect={handleModelChange}
          />
          <Styled.ModelOptionsWrapper>
            <OptionDropdown
              className="option-dropdown"
              label="Duration"
              options={availableDurationOptions}
              selectedValue={duration?.value}
              onSelect={handleDurationChange}
            />
            {isAspectRatioVisible &&
              (hasUploadedImage ? (
                <ImageAspectRatioDropdown
                  className="option-dropdown"
                  options={availableAspectRatioOptions}
                  selectedValue={aiVideoAspectRatio}
                  onSelect={handleImageAspectRatioChange}
                />
              ) : (
                <OptionDropdown
                  className="option-dropdown"
                  label={translate(T.ASPECT_RATIO_LABEL)}
                  options={availableAspectRatioOptions}
                  selectedValue={aiVideoAspectRatio}
                  onSelect={handleAspectRatioChange}
                />
              ))}
            <OptionDropdown
              label="Quality"
              options={availableQualityOptions}
              selectedValue={quality}
              onSelect={handleQualityChange}
              className="quality-dropdown"
            />
          </Styled.ModelOptionsWrapper>
        </Styled.DropdownWrapper>
      </Styled.ModelSection>
    </DisabledWrapper>
  )
}

export default memo(ModelSection)
